import { useTranslation } from "react-i18next";
import type { Invoice, InvoiceCreatePayload } from "../../../../../api/invoices";
import {
  useCreateFreeInvoice,
  useCreateInvoice,
} from "../../../../../hooks/useInvoices";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useOrderingMessage } from "../../useOrderingMessage";

/**
 * Записът от `InvoiceCreateModal` — фактура по сметки, фактура за депозит или
 * свободна фактура.
 *
 * Фактурата за депозит е `POST /invoices` с `deposit_id` и `bill_ids: []` —
 * без бон (бонът на депозита вече е излязъл при вземането).
 *
 * Свободната (`free`) е `POST /invoices/free` — само „По сметка" и без бон
 * (BE `free_invoice.py`, 0.0.389; друг метод е 400). Повторение със загубен
 * отговор BE-то познава само и връща вече издадената, без втора.
 */
interface Options {
  free: boolean;
  onSaved?: (invoice: Invoice) => void;
  /** Фактура за депозит (не свободна) — BE я връзва с депозита и бона му. */
  depositId?: number | null;
}

export function useInvoiceSubmit({ free, onSaved, depositId = null }: Options) {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const createInvoiceMutation = useCreateInvoice();
  const createFreeMutation = useCreateFreeInvoice();

  const isPending = createInvoiceMutation.isPending || createFreeMutation.isPending;

  const submit = async (payload: InvoiceCreatePayload) => {
    if (isPending) return;
    try {
      const created = free
        ? await createFreeMutation.mutateAsync({
            ...payload,
            bill_ids: [],
            payment_method_id: payload.payment_method_id as number,
          })
        : await createInvoiceMutation.mutateAsync(
            depositId !== null ? { ...payload, bill_ids: [], deposit_id: depositId } : payload,
          );
      message.success(free ? t("freeInvoice.issued") : t("invoiceCreate.saveSuccess"));
      onSaved?.(created);
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, t("invoiceCreate.saveError")));
    }
  };

  return { submit, isPending };
}
